import {useRequest} from '@hooks'
import type {
    AgendaRecebimentoAcceptNewBookingDateRequest,
    AgendaRecebimentoBookingDetailsResponse,
    AgendaRecebimentoBookingNFResponse,
    AgendaRecebimentoBookingResumeResponse,
    AgendaRecebimentoReceivingBookingSupplierResponse,
    AgendaRecebimentoReceivingBookingTransportResponse,
    AgendaRecebimentoElectronicInvoiceResponse,
    AgendaRecebimentoRejectNewBookingDateRequest,
    AgendaRecebimentoShippingKnowledgeResponse,
    ClienteHttp,
    Identificador,
} from '../types'

const BASE_URL = '/agenda-recebimento'

// Instanciado sob demanda (e não no topo do módulo) porque @services e @hooks
// se importam em ciclo (AgendaRecebimentoService -> @hooks -> useRequest -> @services);
// chamar useRequest() na primeira avaliação do módulo pode ocorrer antes do
// ciclo terminar de resolver, com useRequest ainda indefinido.
// TODO: Claude 14/09/2026 | remover lazy quando useRequest.ts importar getTokenFromKeycloak direto de ./keycloakService
let cliente: ClienteHttp | undefined

function obterCliente(): ClienteHttp {
    // useRequest não é um hook React (não usa useState/useContext/etc.), é uma
    // fábrica que só coincide com o prefixo "use". A regra estática não sabe
    // disso e acusa chamada de hook fora de componente/hook.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    cliente ??= useRequest()
    return cliente
}

async function getTransports(codigoTrasportadora: Identificador, filialEntrega: Identificador, status: string) {
    return obterCliente().get<AgendaRecebimentoReceivingBookingTransportResponse[]>(`${BASE_URL}/transport`,
        {codigoTrasportadora, filialEntrega, status})
}

async function getSupplier(codigoFornecedor: Identificador, filialEntrega: Identificador, status: string) {
    return obterCliente().get<AgendaRecebimentoReceivingBookingSupplierResponse[]>(`${BASE_URL}/supplier`,
        {codigoFornecedor, filialEntrega, status})
}

async function getShippingKnowledge(agendaId: Identificador) {
    return obterCliente().get<AgendaRecebimentoShippingKnowledgeResponse[]>(`${BASE_URL}/cfrete`, {agendaId})
}

async function getElectronicInvoiceData(agendaId: Identificador) {
    return obterCliente().get<AgendaRecebimentoElectronicInvoiceResponse[]>(`${BASE_URL}/nf`, {agendaId})
}

async function getBookingNFsData(numberNotaFiscal: Identificador, code: Identificador) {
    return obterCliente().get<AgendaRecebimentoBookingNFResponse[]>(`${BASE_URL}/booking-nfs`, {numberNotaFiscal, code})
}

async function getBookingDetailsData(agendaId: Identificador) {
    return obterCliente().get<AgendaRecebimentoBookingDetailsResponse[]>(`${BASE_URL}/booking-details`, {agendaId})
}

async function getBookingResumeData(agendaId: Identificador) {
    return obterCliente().get<AgendaRecebimentoBookingResumeResponse>(`${BASE_URL}/booking-resume`, {agendaId})
}

async function acceptNewSchedulingDate(requestDto: AgendaRecebimentoAcceptNewBookingDateRequest) {
    return obterCliente().post<void, AgendaRecebimentoAcceptNewBookingDateRequest>(`${BASE_URL}/accept`, requestDto)
}

async function rejectNewScheduleDate(requestDto: AgendaRecebimentoRejectNewBookingDateRequest) {
    return obterCliente().post<void, AgendaRecebimentoRejectNewBookingDateRequest>(`${BASE_URL}/reject`, requestDto)
}

export const agendaRecebimentoService = {
    getTransports,
    getSupplier,
    getShippingKnowledge,
    getElectronicInvoiceData,
    getBookingDetailsData,
    acceptNewSchedulingDate,
    rejectNewScheduleDate,
    getBookingNFsData,
    getBookingResumeData,
}

/**
 * Adapter temporário: preserva a factory atual para os consumidores que ainda
 * não migraram para o singleton puro (Tasks 6-8 da Fase 5). Não chama hook React.
 */
export function AgendaRecebimentoService() {
    return agendaRecebimentoService
}
